import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import PageNavbar from "@/components/feature/PageNavbar";
import Footer from "@/pages/home/components/Footer";

const SITE_URL = (import.meta.env.VITE_SITE_URL as string) || "https://isman.co.ke";
const PROFILE_PDF = "/downloads/ISMAN-Company-Profile.pdf";
const BROCHURE_PDF = "/downloads/ISMAN-Services-Brochure.pdf";

const stats = [
  { value: "15+", label: "Years of Excellence" },
  { value: "500+", label: "Projects Completed" },
  { value: "200+", label: "Satisfied Clients" },
  { value: "15", label: "Engineering Services" },
];

const divisions = [
  { title: "3D & 2D Design", img: "/images/home-services/design.webp", to: "/design-services", desc: "AutoCAD floor plans, 3D models, equipment schedules, shop and as-built drawings." },
  { title: "Commercial Kitchens", img: "/images/home-services/kitchen.webp", to: "/commercial-kitchen", desc: "SS304 worktops, extraction canopies, cooking lines, sinks, shelving and cold rooms." },
  { title: "HVAC Systems", img: "/images/home-services/hvac.webp", to: "/hvac-systems", desc: "Split & VRF AC, air handling units, ductwork, ventilation and precision cooling." },
  { title: "Gas & TIG Welding", img: "/images/home-services/welding.webp", to: "/welding-services", desc: "TIG, MIG/MAG and gas welding — food-grade pipework, frames, tanks and structures." },
  { title: "Plumbing", img: "/images/home-services/plumbing.webp", to: "/plumbing-services", desc: "Hot & cold water, drainage, gas and medical gas systems." },
  { title: "Hospital & Pharmacy", img: "/images/home-services/hospital.webp", to: "/hospital-pharmacy", desc: "Dispensing counters, medicine cabinets, trolleys, sterile shelving and nurse stations." },
  { title: "Food Processing", img: "/images/home-services/food.webp", to: "/food-processing", desc: "Mixing tanks, conveyors, processing tables, silos, pasteurisers and filling lines." },
  { title: "Stainless Steel Railing", img: "/images/home-services/railing.webp", to: "/stainless-railing", desc: "Glass & bar balustrades, staircase handrails, pool and mezzanine railings." },
];

const industrial = [
  { title: "Industrial Machinery", slug: "industrial-machinery", desc: "Compressors, vacuum pumps, air conditioning, cold rooms, chillers and BMS." },
  { title: "Preventive Maintenance", slug: "preventive-maintenance", desc: "Scheduled maintenance programmes that keep downtime to a minimum." },
  { title: "Pneumatic & Hydraulic", slug: "pneumatic-hydraulic", desc: "Design, installation, maintenance and fault diagnosis." },
  { title: "Structural Installation", slug: "structural-installation", desc: "Steel structures, platforms, mezzanines and equipment bases." },
  { title: "Food Grade Equipment", slug: "food-grade-equipment", desc: "Mills, conveyors, elevators, silos and dehumidification." },
  { title: "Water Treatment", slug: "water-treatment", desc: "Reverse osmosis, demineralisation, dosing and purification." },
  { title: "System Automation", slug: "system-automation", desc: "PLC, SCADA, dataloggers and real-time alerts." },
];

const missionPoints = [
  "Continuously improving the standards and quality of our services.",
  "Constantly striving to exceed each client's expectations.",
  "Maintaining our dedication to the highest moral and professional principles.",
  "Providing a challenging and secure environment for our people.",
];

const advantages = [
  { icon: "ri-team-line", title: "Expert Manpower", desc: "Mechanical, electrical and electronics technicians constantly under training to handle the latest industrial challenges." },
  { icon: "ri-shield-check-line", title: "Reliability & Durability", desc: "We consider every technical requirement so repairs and installations are reliable, durable and proven against future failures." },
  { icon: "ri-timer-flash-line", title: "Minimal Downtime", desc: "Fast response, accurate diagnostics and efficient execution get your production running with the least disruption." },
  { icon: "ri-hand-heart-line", title: "Honesty is Our Policy", desc: "Full transparency — fair pricing, honest assessments and no shortcuts. Honourable and conscientious to all stakeholders." },
];

const team = ["Mechanical Technicians", "Electrical Engineers", "Electronics Specialists", "PLC / SCADA Experts", "HVAC Technicians", "Fabrication Welders"];

const gallery = [
  "/images/food/mixing-tanks.webp", "/images/kitchen/cooking-range.webp", "/images/hvac/ahu.webp",
  "/images/welding/stainless.webp", "/images/railing/staircase.webp", "/images/water/ro-a.webp",
  "/images/hospital/pharmacy-counter.webp", "/images/structural/platforms.webp", "/images/pneumatic/installation.webp",
];

const steps = [
  { n: "01", t: "Site Survey", d: "We visit, measure and document your space and existing services." },
  { n: "02", t: "Design", d: "2D/3D AutoCAD drawings and equipment schedules for your approval." },
  { n: "03", t: "Fabrication", d: "Built in our Nairobi workshop with quality checks at every stage." },
  { n: "04", t: "Install & Support", d: "Installation, commissioning, training and ongoing maintenance." },
];

function DownloadButtons({ dark = false }: { dark?: boolean }) {
  return (
    <div className="flex flex-col sm:flex-row gap-3">
      <a href={PROFILE_PDF} download className="inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-500 text-white font-bold px-6 py-3 rounded-lg text-sm uppercase tracking-wider transition-colors">
        <i className="ri-file-download-line text-lg"></i> Company Profile (PDF)
      </a>
      <a href={BROCHURE_PDF} download className={`inline-flex items-center justify-center gap-2 font-bold px-6 py-3 rounded-lg text-sm uppercase tracking-wider transition-colors border-2 ${dark ? "border-white/40 text-white hover:border-teal-300 hover:text-teal-200" : "border-teal-600 text-teal-700 hover:bg-teal-50"}`}>
        <i className="ri-book-open-line text-lg"></i> Services Brochure (PDF)
      </a>
    </div>
  );
}

export default function CompanyProfilePage() {
  useSEO({
    title: "Company Profile | ISMAN Engineering Nairobi Kenya — Download PDF",
    description: "ISMAN Engineering company profile — 15+ years of industrial engineering, stainless steel fabrication, HVAC, commercial kitchens and automation in Kenya. Download our company profile and services brochure (PDF).",
    keywords: "ISMAN company profile, engineering company profile Kenya, services brochure PDF, stainless steel fabrication Nairobi",
    canonical: SITE_URL + "/company-profile",
  });

  return (
    <main className="min-h-screen bg-white">
      <PageNavbar />

      {/* Hero */}
      <section className="relative min-h-[78vh] flex items-center overflow-hidden pt-36 pb-20">
        <img src="/images/hero/engineering.webp" alt="ISMAN Engineering facility" className="absolute inset-0 w-full h-full object-cover object-right" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/60 to-slate-900/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img src="/logo.png" alt="ISMAN logo" className="w-14 h-14 rounded-xl bg-white p-1.5" />
              <div>
                <p className="text-white font-extrabold tracking-wide">ISMAN ENGINEERING</p>
                <p className="text-teal-300 text-xs uppercase tracking-widest">Company Limited · Nairobi, Kenya</p>
              </div>
            </div>
            <h1 className="text-white text-4xl md:text-6xl font-extrabold leading-tight">Company <span className="text-teal-400">Profile</span></h1>
            <p className="text-white/75 text-base md:text-lg mt-5 max-w-xl leading-relaxed">
              A team of highly skilled engineers and technicians delivering precision engineering, stainless steel fabrication,
              HVAC, commercial kitchens and industrial installations across Kenya and East Africa.
            </p>
            <div className="mt-8"><DownloadButtons dark /></div>
          </div>
          <a href={PROFILE_PDF} target="_blank" rel="noopener noreferrer" className="hidden lg:block justify-self-end group" title="Open the company profile PDF">
            <div className="relative w-[300px] h-[420px] rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/20 rotate-2 group-hover:rotate-0 transition-transform duration-500">
              <img src="/images/hero/engineering.webp" alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-slate-950/55"></div>
              <div className="absolute inset-0 p-6 flex flex-col">
                <img src="/logo.png" alt="" className="w-11 h-11 rounded-lg bg-white p-1" />
                <div className="mt-auto">
                  <div className="w-10 h-1 bg-teal-400 mb-3"></div>
                  <p className="text-white text-3xl font-extrabold leading-tight">Company<br />Profile</p>
                  <p className="text-teal-200 text-xs mt-2">2026 Edition · 9 pages · PDF</p>
                </div>
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {stats.map((s) => (
            <div key={s.label}>
              <p className="text-teal-400 text-3xl md:text-4xl font-extrabold">{s.value}</p>
              <p className="text-white/60 text-xs uppercase tracking-widest mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Who we are */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-teal-600 text-xs font-bold uppercase tracking-widest">About ISMAN</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2">Who We Are</h2>
            <p className="text-gray-600 mt-5 leading-relaxed">
              <strong>ISMAN COMPANY</strong> is a team of highly skilled engineers and technicians with hands-on experience in industrial
              equipment and production lines. We have built our reputation on delivering engineering excellence that East African industries
              rely on — from food processing plants and HVAC systems to full automation and water treatment solutions.
            </p>
            <p className="text-gray-600 mt-4 leading-relaxed">
              Our deep technical expertise, combined with a commitment to honesty and professionalism, has earned us the trust of over
              <strong> 200 clients</strong> across manufacturing, hospitality, healthcare and agriculture. Every project is treated with the
              same care and precision — whether it's a routine maintenance call or a full industrial installation.
            </p>
            <p className="text-gray-600 mt-4 leading-relaxed">
              Our in-house Nairobi workshop gives us direct control of quality, lead times and cost — from site survey and AutoCAD drawing to
              fabrication, installation, commissioning and after-sales maintenance.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src="/images/kitchen/installation-team.webp" alt="ISMAN team installing a commercial kitchen" className="col-span-2 w-full h-64 object-cover rounded-xl" />
            <img src="/images/welding/tig.webp" alt="TIG welding" className="w-full h-40 object-cover rounded-xl" />
            <img src="/images/design/3d-model.webp" alt="3D engineering design" className="w-full h-40 object-cover rounded-xl" />
          </div>
        </div>
      </section>

      {/* Mission / ethos / policy */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-teal-600 text-xs font-bold uppercase tracking-widest">What drives us</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2">Mission, Ethos &amp; Policy</h2>
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-7 border border-gray-100">
              <i className="ri-flag-2-line text-2xl text-teal-600"></i>
              <h3 className="font-bold text-gray-900 text-lg mt-3">Mission &amp; Goals</h3>
              <p className="text-gray-500 text-sm mt-2 leading-relaxed">To build our reputation for integrity, excellence and leadership as one of the finest engineering and contracting organisations by:</p>
              <ul className="mt-4 space-y-2">
                {missionPoints.map((m) => (
                  <li key={m} className="flex gap-2 text-sm text-gray-700"><i className="ri-check-line text-teal-600 mt-0.5"></i>{m}</li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-xl p-7 border border-gray-100">
              <i className="ri-award-line text-2xl text-teal-600"></i>
              <h3 className="font-bold text-gray-900 text-lg mt-3">Company Ethos</h3>
              <p className="text-gray-500 text-sm mt-2 leading-relaxed">Our ethos is to be regarded by all our stakeholders as <strong className="text-gray-800">honourable and conscientious</strong>.</p>
              <blockquote className="mt-4 border-l-4 border-teal-500 pl-4 text-gray-700 italic text-sm">"We do what we say, and we say what we do — with full accountability to every client, partner, and team member."</blockquote>
              <div className="flex flex-wrap gap-2 mt-5">
                {["Integrity", "Accountability", "Professionalism", "Excellence"].map((v) => (
                  <span key={v} className="bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full">{v}</span>
                ))}
              </div>
            </div>
            <div className="bg-slate-900 rounded-xl p-7 text-white">
              <i className="ri-scales-3-line text-2xl text-teal-300"></i>
              <h3 className="font-bold text-lg mt-3">Our Policy</h3>
              <p className="text-teal-200 text-xl font-extrabold mt-2">"Honesty is the best Policy"</p>
              <p className="text-white/70 text-sm mt-3 leading-relaxed">Transparency in every engagement is non-negotiable. Long-term relationships are built on trust, clear communication and consistent delivery — no shortcuts, no hidden costs.</p>
              <ul className="mt-4 grid grid-cols-2 gap-2 text-sm">
                {["Transparent pricing", "Clear communication", "On-time delivery", "Full documentation"].map((x) => (
                  <li key={x} className="flex gap-1.5 text-white/85"><i className="ri-checkbox-circle-line text-teal-300"></i>{x}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why choose us + formula */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-teal-600 text-xs font-bold uppercase tracking-widest">The ISMAN advantage — precision you can trust</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2">Why Choose Us</h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto text-sm leading-relaxed">We don't just fix machines — we ensure your entire operation performs at its best. Our engineers are continuously trained on the latest technologies.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {advantages.map((a) => (
              <div key={a.title} className="rounded-xl border border-gray-100 p-6 hover:border-teal-200 transition-colors">
                <div className="w-12 h-12 rounded-lg bg-teal-50 flex items-center justify-center"><i className={`${a.icon} text-2xl text-teal-600`}></i></div>
                <h3 className="font-bold text-gray-900 mt-4">{a.title}</h3>
                <p className="text-gray-500 text-sm mt-2 leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 grid lg:grid-cols-2 gap-10 items-center bg-gray-50 rounded-2xl p-8 md:p-10">
            <div>
              <span className="text-teal-600 text-xs font-bold uppercase tracking-widest">The secret behind our success</span>
              <h3 className="text-2xl font-extrabold text-gray-900 mt-2">Our Formula</h3>
              <p className="text-gray-600 mt-4 text-sm leading-relaxed">Our key factor in machine repair relies on our manpower. Our most valuable resources are highly skilled mechanical, electrical and electronics technicians who are constantly under training to offer the latest tools to solve new-generation machine breakdowns — backed by knowledge gained through hands-on experience on actual industrial machine repair.</p>
              <p className="text-gray-600 mt-3 text-sm leading-relaxed">At ISMAN COMPANY we consider every technical requirement so that the outcome of our work is as reliable and durable as possible — proven against future mechanical or electrical failures.</p>
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-4">Our Team</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {team.map((t) => (
                  <div key={t} className="bg-slate-900 text-white text-xs font-semibold rounded-lg px-3 py-4 text-center">{t}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Divisions */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-teal-600 text-xs font-bold uppercase tracking-widest">What we deliver</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2">Engineering Divisions</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {divisions.map((d) => (
              <Link key={d.title} to={d.to} className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-teal-200 transition-all">
                <div className="aspect-[16/10] overflow-hidden"><img src={d.img} alt={d.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                <div className="p-5">
                  <h3 className="font-bold text-gray-900 group-hover:text-teal-700">{d.title}</h3>
                  <p className="text-gray-500 text-xs mt-1.5 leading-relaxed">{d.desc}</p>
                </div>
              </Link>
            ))}
          </div>
          <h3 className="text-xl font-extrabold text-gray-900 mt-14 mb-6">Industrial Engineering Services</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {industrial.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="group flex gap-3 bg-white rounded-xl p-3 border border-gray-100 hover:border-teal-200 transition-colors">
                <img src={`/images/services-grid/${s.slug}.webp`} alt={s.title} loading="lazy" className="w-20 h-20 rounded-lg object-cover shrink-0" />
                <div>
                  <h4 className="font-bold text-gray-900 text-sm group-hover:text-teal-700">{s.title}</h4>
                  <p className="text-gray-500 text-xs mt-1 leading-relaxed">{s.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery + process */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-teal-600 text-xs font-bold uppercase tracking-widest">Selected equipment &amp; installations</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2">Our Work</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {gallery.map((g) => (
              <img key={g} src={g} alt="ISMAN project" loading="lazy" className="w-full h-44 md:h-56 object-cover rounded-xl" />
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
            {steps.map((s) => (
              <div key={s.n} className="rounded-xl bg-gray-50 p-6">
                <p className="text-teal-600 text-3xl font-extrabold">{s.n}</p>
                <h4 className="font-bold text-gray-900 mt-2">{s.t}</h4>
                <p className="text-gray-500 text-sm mt-1.5 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Downloads */}
      <section className="relative py-20 px-6 overflow-hidden">
        <img src="/images/hero/welding.webp" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/85"></div>
        <div className="relative max-w-5xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-white text-3xl md:text-4xl font-extrabold">Download Our Documents</h2>
            <p className="text-white/70 mt-4 leading-relaxed">Share our company profile and full services brochure with your team, procurement or board. Free PDF downloads — print-ready A4.</p>
            <div className="mt-8"><DownloadButtons dark /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[{ t: "Company Profile", s: "9 pages · A4 PDF", href: PROFILE_PDF, img: "/images/hero/engineering.webp" },
              { t: "Services Brochure", s: "8 pages · A4 PDF", href: BROCHURE_PDF, img: "/images/hero/welding.webp" }].map((d) => (
              <a key={d.t} href={d.href} target="_blank" rel="noopener noreferrer" className="group block rounded-xl overflow-hidden ring-1 ring-white/15 hover:ring-teal-300 transition">
                <div className="relative aspect-[3/4]">
                  <img src={d.img} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-slate-950/55 group-hover:bg-slate-950/40 transition-colors"></div>
                  <div className="absolute inset-0 p-4 flex flex-col">
                    <img src="/logo.png" alt="" className="w-9 h-9 rounded-md bg-white p-1" />
                    <div className="mt-auto">
                      <p className="text-white font-extrabold text-lg leading-tight">{d.t}</p>
                      <p className="text-teal-200 text-xs mt-1">{d.s}</p>
                      <p className="text-white/80 text-xs mt-2 flex items-center gap-1"><i className="ri-eye-line"></i> View online</p>
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 px-6 bg-teal-600">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-white">
          <div>
            <h3 className="text-2xl font-extrabold">Ready to work with ISMAN?</h3>
            <p className="text-teal-100 mt-1 text-sm">Free site survey and quotation — Nairobi Industrial Area, Kenya.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href="tel:0724114555" className="bg-white text-teal-700 font-bold px-6 py-3 rounded-lg text-sm flex items-center gap-2"><i className="ri-phone-line"></i> 072 411 4555</a>
            <a href="mailto:info@isman.co.ke" className="border-2 border-white/60 font-bold px-6 py-3 rounded-lg text-sm flex items-center gap-2"><i className="ri-mail-line"></i> info@isman.co.ke</a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
